import React, { useEffect, useState, useContext } from "react";
import { Section } from "../../../atoms/section";
import { Header } from "../../../molecules/header";
import { SavingsCard } from "../../../molecules/savings-card";
import { Flex } from "../../../atoms/flex";
import { useStore } from "../../../../store";
import { HashLink as Link } from "react-router-hash-link";
import { SECTIONS } from "../../../../constants/sections";
import "./savings-over-time.css";
import {
  TextAlignment,
  TextInput,
  TextInputMode,
} from "../../../molecules/text-input";
import { Button } from "../../../atoms/button/button";
import { AnalyticsContext } from "../../../../utils/analytics/analytics-context";
import { UNITS } from "../../../../constants/units";

interface savingsByYearProps {
  economy: number | null;
  water: number | null;
  carbonDioxide: number | null;
  microplastics: number | null;
}

export const SavingsOverTime = () => {
  const { track } = useContext(AnalyticsContext);
  const [numberOfMimboxes, setNumberOfMimboxes] = useState<number>(25);
  const [years, setYears] = useState<number>(5);
  const [savingsByYear, setSavingsByYear] = useState<savingsByYearProps>();
  const { data, loading } = useStore();

  const calculateSavingsByYearPerValue = (value: number) => {
    if (data?.periodInDays && value) {
      return numberOfMimboxes * years * ((value / data.periodInDays) * 365);
    } else {
      return 0;
    }
  };

  const calculateEconomicSavingsByYearPerValue = () => {
    if (data) {
      const {
        waterSavedM3: totalWaterSavings,
        energySaved: totalEnergySavings,
        timeSinceBeginning,
        waterPrice,
        energyPrice,
        waterPriceIncrease,
        energyPriceIncrease,
      } = data;
      const yearsSinceBeginning = timeSinceBeginning / 365 / 24 / 3600 / 1000;
      const yearlyWaterSavings = totalWaterSavings / yearsSinceBeginning;
      const yearlyEnergySavings = totalEnergySavings / yearsSinceBeginning;

      let economicSavings = 0;

      for (let year = 1; year < years + 1; year++) {
        const val = Math.pow(1 + waterPriceIncrease, year - 1);
        if (val < 2) {
          const yearlyIncrease =
            yearlyWaterSavings *
              waterPrice *
              Math.pow(1 + waterPriceIncrease, year - 1) +
            yearlyEnergySavings *
              energyPrice *
              Math.pow(1 + energyPriceIncrease, year - 1);

          economicSavings += yearlyIncrease;
        } else {
          const yearlyIncrease =
            yearlyWaterSavings * waterPrice * 2 +
            yearlyEnergySavings *
              energyPrice *
              Math.pow(1 + energyPriceIncrease, year - 1);
          economicSavings += yearlyIncrease;
        }
      }
      return numberOfMimboxes * economicSavings;
    } else {
      return 0;
    }
  };

  const calculateSavingsByYear = () => {
    if (numberOfMimboxes && years && data) {
      track("calculate_savings", {
        years,
        numberOfMimboxes,
      });
      setSavingsByYear({
        economy: calculateEconomicSavingsByYearPerValue(),
        water: calculateSavingsByYearPerValue(data.waterSavedL),
        carbonDioxide: calculateSavingsByYearPerValue(
          data.carbonDioxideSavings
        ),
        microplastics: calculateSavingsByYearPerValue(
          data.microPlasticsSavings
        ),
      });
    }
  };

  useEffect(() => {
    if (!savingsByYear) {
      calculateSavingsByYear();
    }
  });

  const economicSavingsInKSEK = (savingsByYear?.economy || 0) > 10000;

  return (
    <>
      <Section
        className="wave-white small-padding"
        id={SECTIONS.SAVINGS_OVER_TIME}
      >
        <Header
          title={SECTIONS.SAVINGS_OVER_TIME}
          subTitle="Fyll i antal Mimboxar och år för att se hur siffrorna kan komma att se ut, baserat på data från pågående pilot. "
          className="sub-text"
          loading={loading}
        />
        <Flex className="section-content input-section">
          <Flex>
            <Flex className="savings-input-row">
              <p>Mimbox antal:</p>
              <TextInput
                value={numberOfMimboxes}
                className="savings-input-field"
                mode={TextInputMode.NUMBER}
                textAlignment={TextAlignment.CENTER}
                onChange={(event) =>
                  setNumberOfMimboxes(parseInt(event.target.value))
                }
              ></TextInput>
            </Flex>
            <Flex className="savings-input-row">
              <p>År:</p>
              <TextInput
                value={years}
                className="savings-input-field"
                mode={TextInputMode.NUMBER}
                textAlignment={TextAlignment.CENTER}
                onChange={(event) => setYears(parseInt(event.target.value))}
              ></TextInput>
            </Flex>
          </Flex>
          <Button
            className="savings-input-button"
            onClick={() => calculateSavingsByYear()}
            disabled={!(numberOfMimboxes && years)}
          >
            Beräkna
          </Button>
        </Flex>
        <Flex className="section-content">
          <Link to={`#${SECTIONS.ECONOMY}`}>
            <SavingsCard
              value={
                Math.round(
                  (savingsByYear?.economy || 0) /
                    (economicSavingsInKSEK ? 1000 : 1)
                ) + (economicSavingsInKSEK ? "K" : "")
              }
              unit={"SEK"}
              isLoading={loading}
              icon="money"
            />
          </Link>
          <Link to={`#${SECTIONS.WATER}`}>
            <SavingsCard
              value={Math.round((savingsByYear?.water || 0) / 1000)}
              unit={UNITS.CUBIC}
              isLoading={loading}
              icon="water"
            />
          </Link>
          <Link to={`#${SECTIONS.CARBON_DIOXIDE}`}>
            <SavingsCard
              value={((savingsByYear?.carbonDioxide || 0) / 1000).toFixed(1)}
              unit={UNITS.TON}
              isLoading={loading}
              icon="carbon-dioxide"
            />
          </Link>
          <Link to={`#${SECTIONS.MICROPLASTICS}`}>
            <SavingsCard
              value={Math.round((savingsByYear?.microplastics || 0) / 1000)}
              unit={UNITS.KILO}
              isLoading={loading}
              icon="microplastics"
            />
          </Link>
        </Flex>
      </Section>
    </>
  );
};
